// iter 68 — Evaluation Insights tab.
// Transparency layer between Client Inputs and Implementation. Reads only
// from the SmartEvaluate scoring-engine output already stored on the
// assessment (no recomputation, no new API calls). Surfaces per-warehouse
// complexity + driver weightage + recommendation reasoning so consultants
// can explain WHY SmartEvaluate reached each recommendation.
import React, { useState, useEffect } from "react";
import { getAssessmentNarrative, reprocessAssessment } from "../lib/adminApi";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import {
  deriveCmStrategy,
  CM_STRATEGY_LABELS,
  CM_STRATEGY_DESCRIPTIONS,
  CM_APPROACH_DESCRIPTIONS,
} from "./cmStrategy";

const WH_LABELS = {
  cdc: "Central Distribution Center",
  rdc: "Regional Distribution Center",
  manufacturing: "Manufacturing Plant Warehouse",
  ecommerce: "E-commerce Fulfillment Center",
  spare_parts: "Spare Parts / Service Warehouse",
  satellite: "Small / Satellite Warehouse",
};

// The evaluation unit is now the Warehouse GROUP (keyed by stable group id).
// Resolve a unit's display label + eyebrow from assessment.warehouse_groups;
// fall back to the legacy warehouse-type labels for pre-group assessments.
function unitInfo(assessment, unitKey) {
  const g = (assessment?.warehouse_groups || []).find((x) => x.id === unitKey);
  if (g) {
    return {
      label: g.name || unitKey,
      eyebrow: (g.assessment_type || "group").replace(/_/g, " "),
    };
  }
  return { label: WH_LABELS[unitKey] || unitKey, eyebrow: unitKey };
}

const SECTION_LABEL = {
  org_score: "Organisation",
  inbound_score: "Inbound",
  outbound_score: "Outbound",
  inventory_score: "Inventory",
  automation_score: "Automation",
  integration_score: "Integration",
  reporting_score: "Reporting / Analytics",
  pain_points_score: "Pain Points / Urgency",
};

const STRATEGY_DESCR = {
  single_solution: "A single SAP warehouse solution is recommended network-wide because the scored profiles converge on the same capability band.",
  hybrid: "A hybrid strategy is recommended — different warehouse groups landed on different SAP solutions based on their complexity and throughput profile.",
  hybrid_solution: "A hybrid strategy is recommended — different warehouse groups landed on different SAP solutions based on their complexity and throughput profile.",
  mixed: "A mixed strategy — facilities land in different capability bands, requiring differentiated solutions per site.",
};

const SOLUTION_ORDER = [
  { key: "SAP Stock Room Management", label: "SAP Stock Room", short: "StRM" },
  { key: "SAP LGM",                   label: "SAP LGM",       short: "LGM" },
  { key: "SAP EWM Basic",             label: "SAP EWM Basic", short: "EWM Basic" },
  { key: "SAP EWM Advanced",          label: "SAP EWM Advanced", short: "EWM Advanced" },
];

function scoreBand(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return { label: "—", tone: "#6B7280" };
  if (n >= 2.5) return { label: "High", tone: "#B45309" };
  if (n >= 1.5) return { label: "Medium", tone: "#025979" };
  return { label: "Low", tone: "#16A34A" };
}

function buildSolutionMatrix(solutionScores, rec) {
  const src = solutionScores || {};
  const raw = SOLUTION_ORDER.map((s) => ({
    ...s,
    score: Number(src[s.key] || 0),
  }));
  const max = Math.max(0, ...raw.map((r) => r.score));
  return raw.map((r) => ({
    ...r,
    pct: max > 0 ? Math.round((r.score / max) * 100) : 0,
    isRecommended: String(rec || "").trim() === s_strip(r.key),
    isHighest: max > 0 && r.score === max,
  }));
}

function s_strip(k) { return String(k || "").trim(); }

function buildRecommendationCopy(rec, sections, drivers) {
  const autoBand = scoreBand(sections?.automation_score).label;
  const integBand = scoreBand(sections?.integration_score).label;
  const invBand = scoreBand(sections?.inventory_score).label;
  const outBand = scoreBand(sections?.outbound_score).label;
  const signals = [];
  if (autoBand === "High") signals.push("high automation readiness");
  if (integBand === "High") signals.push("deep integration needs");
  if (invBand === "High") signals.push("complex inventory");
  if (outBand === "High") signals.push("high-throughput outbound");
  if (!signals.length && drivers?.length) {
    signals.push(...drivers.slice(0, 2).map((d) => d.label.toLowerCase()));
  }
  const tail = signals.length
    ? `warehouses with ${signals.slice(0, 3).join(", ")}`
    : "the capability profile observed at this site";
  const sol = s_strip(rec) || "the recommended SAP solution";
  return `Recommended for ${tail}. ${sol} matches this profile across the scored dimensions.`;
}

function buildDriverWeightage(triggers) {
  // Convert raw trigger activations into percentage weightage, grouped
  // into business-readable drivers. Only shows the drivers that actually
  // fired for this site.
  const t = triggers || {};
  const groups = [
    { key: "inventory", label: "Inventory Complexity", fields: ["has_high_inventory_complexity", "has_hu_requirement", "has_cross_docking_requirement"] },
    { key: "outbound", label: "Outbound Throughput", fields: ["has_high_outbound_complexity", "has_advanced_picking_requirement", "has_vas_requirement"] },
    { key: "inbound", label: "Inbound Complexity", fields: ["has_high_inbound_complexity"] },
    { key: "labor", label: "Labor Management", fields: ["has_labor_management_need"] },
    { key: "integration", label: "Integration Depth", fields: ["has_complex_integration", "has_mfg_integration", "has_high_integration_complexity"] },
    { key: "automation", label: "Automation Fit", fields: ["has_automation_readiness", "has_mfs_need", "has_advanced_automation"] },
    { key: "yard", label: "Yard & Dock Management", fields: ["has_yard_management_requirement"] },
    { key: "rfid", label: "RFID Enablement", fields: ["has_rfid_requirement"] },
    { key: "analytics", label: "Operational Intelligence & Analytics", fields: ["has_advanced_reporting_requirement"] },
    { key: "production", label: "Production Integration", fields: ["has_production_integration"] },
    // New specialized EWM-Advanced capability drivers (June 2026 trigger wiring)
    { key: "reverse_logistics", label: "Reverse Logistics", fields: ["has_reverse_logistics"] },
    { key: "deconsolidation", label: "Deconsolidation", fields: ["has_deconsolidation"] },
    { key: "cartonization", label: "Cartonization", fields: ["has_cartonization"] },
    { key: "hazmat", label: "Hazardous Materials", fields: ["has_hazardous_materials"] },
    { key: "bonded", label: "Bonded Warehouse", fields: ["has_bonded_warehouse"] },
  ];
  const weights = groups.map((g) => ({
    key: g.key,
    label: g.label,
    score: g.fields.reduce((n, f) => n + Number(t[f] || 0), 0),
  })).filter((g) => g.score > 0);
  const total = weights.reduce((n, g) => n + g.score, 0);
  if (total === 0) return [];
  return weights
    .map((g) => ({ ...g, pct: Math.round((g.score / total) * 100) }))
    .sort((a, b) => b.pct - a.pct);
}

function buildRecommendationLogic(rec, flags, tags) {
  const bits = [];
  const f = flags || {};
  const sig = f.signals || {};
  if (String(rec || "").includes("Advanced")) {
    if (f.advanced_threshold) bits.push("Capability scores crossed the EWM Advanced threshold across multiple sections.");
    if (sig.high_integration) bits.push("Integration depth indicates the need for Advanced's orchestration capabilities.");
    if (sig.high_automation) bits.push("Automation readiness justifies Advanced's material flow + monitoring features.");
  } else if (String(rec || "").includes("Basic")) {
    bits.push("Capability profile sits solidly in the EWM Basic band — core functionality without Advanced overhead.");
  } else if (String(rec || "").includes("Stock")) {
    bits.push("Stock Room Management covers the essential inventory discipline without EWM investment.");
  } else if (String(rec || "").includes("LGM")) {
    bits.push("SAP LGM is selected because of the material-flow / production-integration profile.");
  }
  if (f.lean_override) bits.push("Lean override applied — footprint lean enough to downshift capability band.");
  if (f.safety_downgrade) bits.push("Safety downgrade applied — urgency / risk signals pulled the recommendation to a safer band.");
  if (f.brownfield_guardrail) bits.push("Brownfield guardrail active — existing landscape constraints shape the recommendation.");
  if (Array.isArray(tags) && tags.length) {
    bits.push("Decision tags: " + tags.slice(0, 4).join(" · "));
  }
  return bits.length ? bits : ["The recommendation matches the capability profile for this warehouse type."];
}

function confidenceFor(assessment, siteKey) {
  const capScores = (assessment?.capability_scores_by_site || {})[siteKey] || {};
  const secScores = (assessment?.section_scores_by_site || {})[siteKey] || {};
  const triggers = (assessment?.trigger_activations_by_site || {})[siteKey] || {};
  const solScores = (assessment?.raw_solution_scores_by_site || {})[siteKey]
    || (assessment?.solution_scores_by_site || {})[siteKey] || {};
  const tagCount = ((assessment?.explanation_tags_by_site || {})[siteKey] || []).length;
  // Base signal: how many scoring dimensions actually have data?
  const filled = [
    Object.keys(capScores).length > 0,
    Object.keys(secScores).length > 0,
    Object.keys(triggers).length > 0,
    tagCount > 0,
  ].filter(Boolean).length;
  // Spread signal: how decisive is the top solution vs the runner-up?
  const ordered = Object.values(solScores).map(Number).filter(Number.isFinite).sort((a, b) => b - a);
  let spread = 0;
  if (ordered.length >= 2 && ordered[0] > 0) {
    spread = (ordered[0] - ordered[1]) / ordered[0];
  } else if (ordered.length === 1 && ordered[0] > 0) {
    spread = 1;
  }
  const decisive = spread >= 0.30;     // top option beats runner-up by ≥30%
  const moderate = spread >= 0.10;     // ≥10%
  if (filled >= 3 && decisive) return { label: "High",   tone: "#16A34A" };
  if (filled >= 2 && moderate) return { label: "Medium", tone: "#D97706" };
  if (filled >= 2)             return { label: "Medium", tone: "#D97706" };
  return { label: "Low", tone: "#DC2626" };
}

function Chip({ children, tone = "#6B7280", testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border"
      style={{ color: tone, borderColor: tone + "40", background: tone + "10" }}
    >
      {children}
    </span>
  );
}

function OverallStrategyInsight({ assessment }) {
  const type = assessment?.overall_strategy_type || "single_solution";
  const summary = assessment?.overall_strategy_summary
    || "A recommended strategy will appear here once SmartEvaluate scoring completes.";
  const descr = STRATEGY_DESCR[type] || STRATEGY_DESCR.single_solution;
  const pretty = {
    single_solution: "Single Solution (Standardized)",
    hybrid: "Hybrid Strategy",
    hybrid_solution: "Hybrid Strategy",
    mixed: "Mixed Strategy",
  }[type] || type;
  return (
    <section
      data-testid="insights-overall-strategy"
      className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
          Overall Strategy Insight
        </h3>
        <Chip tone="#025979" testid="insights-strategy-chip">{pretty}</Chip>
      </div>
      <p className="text-[13px] text-[#0F172A] font-medium leading-relaxed" data-testid="insights-strategy-summary">{summary}</p>
      <p className="text-[12.5px] text-[#374151] leading-relaxed mt-2">{descr}</p>
    </section>
  );
}

// Business drivers as bullet chips — uses the explanation_tags directly
// because those are already human-written ("High inventory complexity",
// "Complex integration landscape"…). Fallback to the trigger-weightage
// buckets when tags are empty.
function driverChipsFor(explanationTags, weightageDrivers) {
  const tags = Array.isArray(explanationTags) ? explanationTags : [];
  if (tags.length) return tags.slice(0, 5);
  return (weightageDrivers || []).slice(0, 4).map((d) => d.label);
}

function SiteInsightCard({ assessment, siteKey }) {
  const info = unitInfo(assessment, siteKey);
  const label = info.label;
  const complexity = (assessment?.complexity_scores_by_site || {})[siteKey] || {};
  const sections = (assessment?.section_scores_by_site || {})[siteKey] || {};
  const rec = (assessment?.final_recommendation_by_site || {})[siteKey];
  const deploy = (assessment?.deployment_recommendation_by_site || {})[siteKey];
  const flags = (assessment?.decision_flags_by_site || {})[siteKey] || {};
  const tags = (assessment?.explanation_tags_by_site || {})[siteKey] || [];
  const triggers = (assessment?.trigger_activations_by_site || {})[siteKey] || {};
  const solScores = (assessment?.raw_solution_scores_by_site || {})[siteKey]
    || (assessment?.solution_scores_by_site || {})[siteKey] || {};
  const weightageDrivers = buildDriverWeightage(triggers);
  const driverChips = driverChipsFor(tags, weightageDrivers);
  const logic = buildRecommendationLogic(rec, flags, tags);
  const matrix = buildSolutionMatrix(solScores, rec);
  const recCopy = buildRecommendationCopy(rec, sections, weightageDrivers);
  const backendConf = (assessment?.confidence_by_site || {})[siteKey];
  const conf = backendConf
    ? {
        label: backendConf.band,
        tone: backendConf.band === "High" ? "#16A34A" : backendConf.band === "Medium" ? "#D97706" : "#DC2626",
        score: backendConf.score,
        factors: backendConf.factors || [],
      }
    : { ...confidenceFor(assessment, siteKey), score: null, factors: [] };
  const explain = (assessment?.explainability_by_site || {})[siteKey] || {};
  const whyBullets = Array.isArray(explain.why) && explain.why.length ? explain.why : logic;
  const whyNot = Array.isArray(explain.why_not) ? explain.why_not : [];

  const scorePills = [
    { k: "automation_score",  label: "Automation" },
    { k: "integration_score", label: "Integration" },
    { k: "inventory_score",   label: "Inventory" },
    { k: "outbound_score",    label: "Throughput" },
  ];

  return (
    <section
      data-testid={`insights-site-${siteKey}`}
      className="bg-white border border-[#E5E7EB] rounded-sm p-5 flex flex-col gap-4"
    >
      {/* 1. HEADER — site label · solution chip · confidence chip */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[9.5px] uppercase tracking-[0.15em] text-[#9CA3AF]">{String(info.eyebrow).toUpperCase()}</p>
          <h3 className="text-[15px] font-bold text-[#0F172A] leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
            {label}
          </h3>
          {deploy && (
            <p className="text-[10.5px] text-[#6B7280] mt-0.5">{deploy}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {rec && (
            <span
              data-testid={`insights-rec-chip-${siteKey}`}
              className="text-[10.5px] font-semibold text-[#025979] bg-[#E0F2FE] border border-[#025979]/25 rounded-sm px-2 py-0.5 whitespace-nowrap"
            >
              {rec}
            </span>
          )}
          <span
            data-testid={`insights-confidence-${siteKey}`}
            className="text-[9.5px] font-semibold uppercase tracking-wider rounded-sm px-1.5 py-0.5 border"
            style={{ color: conf.tone, borderColor: conf.tone + "40", background: conf.tone + "10" }}
          >
            Confidence · {conf.score != null ? `${conf.score}%` : ""}{conf.score != null ? " · " : ""}{conf.label}
          </span>
        </div>
      </header>

      {/* 2. BUSINESS DRIVERS — bullet chips from real triggers / tags */}
      {driverChips.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Business Drivers</p>
          <ul
            data-testid={`insights-drivers-${siteKey}`}
            className="flex flex-wrap gap-1.5"
          >
            {driverChips.map((d, i) => (
              <li
                key={i}
                className="text-[11px] text-[#0F172A] bg-[#F3F4F6] border border-[#E5E7EB] rounded-sm px-2 py-[3px]"
              >
                • {d}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 2b. SPECIALIZED CAPABILITIES — named EWM Advanced capabilities detected at this site */}
      {(() => {
        const caps = (assessment?.capability_scores_by_site || {})[siteKey] || {};
        const SPECIALIZED = [
          "Returns Management (Reverse Logistics)",
          "Deconsolidation",
          "Cartonization / Packing Optimization",
          "Hazardous Materials Management",
          "Bonded / Customs Warehouse Management",
        ];
        const detected = SPECIALIZED.filter((n) => Number(caps[n] || 0) > 0);
        if (!detected.length) return null;
        return (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Specialized Capabilities</p>
            <ul
              data-testid={`insights-specialized-${siteKey}`}
              className="flex flex-wrap gap-1.5"
            >
              {detected.map((n, i) => (
                <li
                  key={i}
                  className="text-[11px] font-medium text-[#025979] bg-[#E0F2FE] border border-[#025979]/25 rounded-sm px-2 py-[3px]"
                >
                  {n}
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {/* 3. CAPABILITY SCORES — compact inline pills */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Capability Scores</p>
        <div className="flex flex-wrap gap-1.5" data-testid={`insights-scores-${siteKey}`}>
          {scorePills.map((p) => {
            const v = sections[p.k];
            const band = scoreBand(v);
            if (band.label === "—") return null;
            return (
              <span
                key={p.k}
                data-testid={`insights-score-pill-${siteKey}-${p.k}`}
                className="inline-flex items-center gap-1 text-[11px] rounded-sm border px-2 py-[3px]"
                style={{ color: band.tone, borderColor: band.tone + "40", background: band.tone + "0D" }}
              >
                <span className="text-[#374151]">{p.label}:</span>
                <span className="font-semibold uppercase tracking-wider">{band.label}</span>
              </span>
            );
          })}
        </div>
      </div>

      {/* 4. SOLUTION SCORING — compact key:value list, top pick bolded */}
      {matrix.some((m) => m.score > 0) && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Solution Scoring</p>
          <ul
            data-testid={`insights-matrix-${siteKey}`}
            className="text-[12px] grid grid-cols-2 gap-x-5 gap-y-1"
          >
            {matrix.map((m) => {
              const isTop = m.isHighest && m.score > 0;
              return (
                <li
                  key={m.key}
                  data-testid={`insights-matrix-row-${siteKey}-${m.short.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center justify-between gap-3 ${isTop ? "" : "text-[#374151]"}`}
                >
                  <span className={isTop ? "text-[#025979] font-semibold" : ""}>{m.label}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className={`tabular-nums ${isTop ? "text-[#025979] font-bold" : "text-[#0F172A]"}`}>
                      {m.score.toFixed(0)}
                    </span>
                    {isTop && (
                      <span
                        data-testid={`insights-matrix-top-${siteKey}`}
                        className="text-[9px] font-semibold uppercase tracking-wider text-[#025979] bg-[#E0F2FE] border border-[#025979]/35 rounded-sm px-1.5 py-[1px]"
                      >
                        Top Pick
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* 5. WHY THIS RECOMMENDATION — bullets + dynamic closing sentence */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Why this recommendation</p>
        <ul className="space-y-1 text-[12px] text-[#111827] leading-relaxed" data-testid={`insights-logic-${siteKey}`}>
          {whyBullets.slice(0, 5).map((l, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-[#025979] mt-0.5">•</span>
              <span>{l}</span>
            </li>
          ))}
        </ul>
        <p className="text-[11.5px] text-[#374151] italic mt-2" data-testid={`insights-rec-copy-${siteKey}`}>
          {recCopy}
        </p>
      </div>

      {/* 5b. WHY NOT — why the runner-up SAP tiers were not chosen (backend explainability) */}
      {whyNot.length > 0 && (
        <div data-testid={`insights-whynot-${siteKey}`}>
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Why not the alternatives</p>
          <ul className="space-y-1 text-[12px] text-[#374151] leading-relaxed">
            {whyNot.map((w, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`insights-whynot-row-${siteKey}-${i}`}>
                <span className="text-[#9CA3AF] mt-0.5">✕</span>
                <span><span className="font-semibold text-[#0F172A]">{w.solution}</span> — {w.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 5c. CONFIDENCE FACTORS — deterministic decision factors behind the score */}
      {conf.factors && conf.factors.length > 0 && (
        <div data-testid={`insights-confidence-factors-${siteKey}`}>
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">
            Confidence Factors {conf.score != null ? `· ${conf.score}% overall` : ""}
          </p>
          <ul className="space-y-1.5 text-[11.5px] text-[#374151]">
            {conf.factors.map((f) => (
              <li key={f.key} className="flex items-start gap-2" data-testid={`insights-cfactor-${siteKey}-${f.key}`}>
                <span
                  className="shrink-0 tabular-nums font-semibold text-[10px] w-9 text-right"
                  style={{ color: f.value_pct >= 75 ? "#16A34A" : f.value_pct >= 50 ? "#D97706" : "#DC2626" }}
                >
                  {f.value_pct}%
                </span>
                <span className="min-w-0">
                  <span className="font-semibold text-[#0F172A]">{f.label}</span>
                  <span className="text-[#6B7280]"> — {f.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Optional flags footer */}
      {(flags.safety_downgrade || flags.brownfield_guardrail || flags.baseline_applied) && (
        <div className="pt-3 border-t border-[#E5E7EB] flex flex-wrap gap-1.5" data-testid={`insights-flags-${siteKey}`}>
          {flags.safety_downgrade && <Chip tone="#B45309">Safety Downgrade</Chip>}
          {flags.brownfield_guardrail && <Chip tone="#B45309">Brownfield Guardrail</Chip>}
          {flags.baseline_applied && <Chip tone="#B45309">Baseline Applied</Chip>}
        </div>
      )}
    </section>
  );
}

// iter (June 2026) — Current Landscape & Architecture Context.
// Supporting insight block derived from the Step 2 "Current SAP Landscape"
// answers. Reads ONLY from the recommendation payload already on the
// assessment (top-level fields, or the `current_sap_landscape` object).
// Pure display — no recomputation, no scoring/recommendation impact.
const ALIGNMENT_BADGE = {
  aligned: { label: "Aligned", tone: "#16A34A" },
  differs_from_engine: { label: "Target Differs from Engine Recommendation", tone: "#B45309" },
  not_specified: { label: "Best Fit Evaluation", tone: "#025979" },
};

function readLandscape(assessment) {
  const ls = assessment?.current_sap_landscape || {};
  const pick = (k) => {
    const v = ls[k] ?? assessment?.[k];
    return v == null || v === "" ? null : v;
  };
  return {
    current_landscape_summary: pick("current_landscape_summary"),
    target_solution_direction: pick("target_solution_direction"),
    architecture_guidance: pick("architecture_guidance"),
    transformation_approach: pick("transformation_approach"),
    hosting_model: pick("hosting_model"),
    target_alignment_status: pick("target_alignment_status"),
  };
}

function LandscapeRow({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold sm:w-48 shrink-0 pt-[1px]">
        {label}
      </span>
      <span className="text-[12.5px] text-[#0F172A] leading-relaxed">{value}</span>
    </div>
  );
}

function CurrentLandscapeContext({ assessment }) {
  const ls = readLandscape(assessment);
  // Current-state only. Hosting Model + Best-Fit Alignment are future-state
  // recommendations and live in the Hosting Advisory card above — excluded here
  // to avoid mixing current facts with future-state guidance / duplication.
  const hasContent = [
    ls.current_landscape_summary,
    ls.target_solution_direction,
    ls.architecture_guidance,
  ].some(Boolean);
  if (!hasContent) return null;
  return (
    <section
      data-testid="insights-current-landscape"
      className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 border-l-2 border-l-[#025979]"
    >
      <h3
        className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3"
        style={{ fontFamily: "Chivo, sans-serif" }}
        data-testid="insights-current-landscape-title"
      >
        Current Landscape Context
      </h3>
      <div className="space-y-2.5">
        <LandscapeRow label="Current Landscape" value={ls.current_landscape_summary} testid="landscape-current" />
        <LandscapeRow label="Target Direction" value={ls.target_solution_direction} testid="landscape-target" />
        <LandscapeRow label="Architecture Guidance" value={ls.architecture_guidance} testid="landscape-architecture" />
      </div>
    </section>
  );
}

// iter (June 2026) — Hosting Advisory (Customer Preference vs Architect Best-Fit).
// Pure display of the additive `hosting_advisory` payload. Customer preferred
// hosting is never overwritten — surfaced alongside the architect best-fit.
const HOSTING_ALIGN_BADGE = {
  aligned: { label: "Aligned", tone: "#16A34A" },
  advisory: { label: "Advisory — Differs", tone: "#B45309" },
  not_specified: { label: "Best-Fit Evaluation", tone: "#025979" },
};

function HostingAdvisory({ assessment }) {
  const h = assessment?.hosting_advisory;
  if (!h || (!h.customer_preferred_hosting && !h.architect_best_fit_hosting)) return null;
  const badge = HOSTING_ALIGN_BADGE[h.hosting_alignment] || HOSTING_ALIGN_BADGE.not_specified;
  return (
    <section
      data-testid="insights-hosting-advisory"
      className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 border-l-2 border-l-[#025979]"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <h3
          className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Hosting Advisory
        </h3>
        <span
          data-testid="insights-hosting-alignment-badge"
          className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider rounded-sm px-1.5 py-0.5 border"
          style={{ color: badge.tone, borderColor: badge.tone + "40", background: badge.tone + "10" }}
        >
          {badge.label}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-sm px-4 py-3" data-testid="insights-hosting-customer">
          <p className="text-[9.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">Customer Preferred</p>
          <p className="text-[13px] font-bold text-[#0F172A]">{h.customer_preferred_hosting || "Not specified"}</p>
        </div>
        <div className="bg-[#E0F2FE] border border-[#025979]/25 rounded-sm px-4 py-3" data-testid="insights-hosting-bestfit">
          <p className="text-[9.5px] uppercase tracking-wider text-[#025979] font-semibold mb-1">Architect Best-Fit</p>
          <p className="text-[13px] font-bold text-[#025979]">{h.architect_best_fit_hosting || "—"}</p>
        </div>
      </div>
      {h.hosting_advisory_note && (
        <p className="text-[12px] text-[#374151] leading-relaxed" data-testid="insights-hosting-note">
          {h.hosting_advisory_note}
        </p>
      )}
    </section>
  );
}

// iter (July 2026) — Transformation Recommendation (SAP-standard type).
// Pure display of the additive `transformation_recommendation` payload:
// SAP-standard transformation type + dynamic Reason bullets + Key Assumptions.
const TRANSFORMATION_TONE = {
  "New Implementation (Greenfield)": "#16A34A",
  "System Conversion": "#025979",
  "Selective Data Transition": "#7C3AED",
  "Hybrid Transformation": "#B45309",
};

function TransformationRecommendation({ assessment }) {
  const t = assessment?.transformation_recommendation;
  if (!t || !t.transformation_type) return null;
  const tone = TRANSFORMATION_TONE[t.transformation_type] || "#025979";
  const reasons = Array.isArray(t.reasons) ? t.reasons : [];
  const assumptions = Array.isArray(t.assumptions) ? t.assumptions : [];
  return (
    <section
      data-testid="insights-transformation"
      className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 border-l-2"
      style={{ borderLeftColor: tone }}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <h3
          className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Transformation Recommendation
        </h3>
        <span
          data-testid="insights-transformation-type"
          className="inline-flex items-center text-[11px] font-bold rounded-sm px-2 py-0.5 border"
          style={{ color: tone, borderColor: tone + "40", background: tone + "12" }}
        >
          {t.transformation_type}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {reasons.length > 0 && (
          <div data-testid="insights-transformation-reasons">
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Reason</p>
            <ul className="space-y-1 text-[12px] text-[#111827] leading-relaxed">
              {reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span style={{ color: tone }} className="mt-0.5">•</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {assumptions.length > 0 && (
          <div data-testid="insights-transformation-assumptions">
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Key Assumptions</p>
            <ul className="space-y-1 text-[12px] text-[#374151] leading-relaxed">
              {assumptions.map((a, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#9CA3AF] mt-0.5">•</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

// Consultant action: re-run the SmartEvaluate scoring engine on the latest
// saved answers, then refresh the tab. Lets group edits (e.g. a changed
// Current Warehouse Solution → new derived scenario) reflect in Insights
// without the owner having to resubmit the whole evaluation.
function RerunScoringButton({ assessmentId, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!assessmentId || busy) return;
    setBusy(true);
    try {
      const res = await reprocessAssessment(assessmentId);
      if (res?.status === "ok") {
        toast.success("Scoring re-run — Evaluation Insights updated.");
        onRefresh?.();
      } else {
        toast.error(res?.error || "Scoring could not complete. Check the evaluation answers.");
      }
    } catch (e) {
      toast.error("Failed to re-run scoring. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      data-testid="rerun-scoring-btn"
      onClick={run}
      disabled={busy}
      className="inline-flex items-center gap-1.5 rounded-sm border border-[#025979] text-[#025979] hover:bg-[#E0F2FE] disabled:opacity-60 px-3 py-1.5 text-xs font-semibold transition-colors"
    >
      <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} strokeWidth={2} />
      {busy ? "Re-running…" : "Re-run scoring"}
    </button>
  );
}

export default function EvaluationInsightsTab({ assessment, assessmentId, onRefresh }) {
  if (!assessment) return null;
  // Cloud Migration reuses this tab + card layout but is informational-only —
  // it has no scoring output. Render the CM-specific insight sections instead
  // of the Greenfield/Brownfield scoring cards. GF/BF path below is unchanged.
  if ((assessment.scenario_type || "").toLowerCase() === "cloud_migration") {
    return <CloudMigrationInsights assessment={assessment} assessmentId={assessmentId} onRefresh={onRefresh} />;
  }
  const scoringDone = assessment.scoring_status === "completed";
  if (!scoringDone) {
    return (
      <div
        data-testid="insights-blocked"
        className="bg-amber-50 border border-amber-200 rounded-sm p-5 text-sm text-amber-900 flex items-center justify-between gap-4 flex-wrap"
      >
        <span>Evaluation Insights become available once SmartEvaluate scoring completes. If you've just edited a group, re-run scoring to refresh.</span>
        <RerunScoringButton assessmentId={assessmentId} onRefresh={onRefresh} />
      </div>
    );
  }
  const sites = Object.keys(assessment.final_recommendation_by_site || {});
  return (
    <div data-testid="evaluation-insights-tab" className="max-w-[1100px] mx-auto space-y-5">
      <div className="flex items-center justify-end">
        <RerunScoringButton assessmentId={assessmentId} onRefresh={onRefresh} />
      </div>
      <OverallStrategyInsight assessment={assessment} />
      <TransformationRecommendation assessment={assessment} />
      <HostingAdvisory assessment={assessment} />
      <CurrentLandscapeContext assessment={assessment} />
      {sites.length === 0 ? (
        <p className="text-[12px] text-[#9CA3AF] italic">No warehouse groups scored yet.</p>
      ) : (
        <div
          data-testid="insights-grid"
          className={sites.length === 1
            ? "grid grid-cols-1 max-w-[640px] gap-4"
            : "grid grid-cols-1 md:grid-cols-2 gap-4"}
        >
          {sites.map((s) => <SiteInsightCard key={s} assessment={assessment} siteKey={s} />)}
        </div>
      )}
    </div>
  );
}

export { WH_LABELS, scoreBand, confidenceFor, driverChipsFor, buildRecommendationCopy, readLandscape, ALIGNMENT_BADGE };

// Pure helpers exported for the Executive Summary compressed block.
export function buildKeyEvaluationInsights(assessment) {
  if (!assessment) return [];
  const sites = Object.keys(assessment.final_recommendation_by_site || {});
  const bullets = [];
  sites.slice(0, 3).forEach((s) => {
    const label = unitInfo(assessment, s).label;
    const complexity = ((assessment.complexity_scores_by_site || {})[s] || {}).label;
    const rec = (assessment.final_recommendation_by_site || {})[s];
    if (!rec) return;
    bullets.push(`${label}${complexity ? ` — ${complexity} complexity` : ""} → ${rec}.`);
  });
  const strategyType = assessment.overall_strategy_type;
  if (strategyType === "hybrid" || sites.length > 1) {
    bullets.push("Overall strategy: hybrid — differentiated solutions per warehouse type.");
  } else if (strategyType === "single_solution" || sites.length === 1) {
    const only = (assessment.final_recommendation_by_site || {})[sites[0]];
    if (only) bullets.push(`Overall strategy: standardized on ${only} across the network.`);
  }
  return bullets.slice(0, 4);
}



// ===========================================================================
// Cloud Migration — Evaluation Insights (informational; reuses card layout).
// Migration Snapshot + advisory Complexity + Risk Areas + Consultant Focus are
// derived from the captured answers (display-only, no scoring impact). The
// Recommended Direction reuses the existing narrative API + Recommendation
// Library (no recommendation logic is duplicated here).
// ===========================================================================
const CM_LANDSCAPE_SITE = "cm_landscape";

const CM_COMPLEXITY_TONE = {
  Low: "#16A34A",
  Medium: "#025979",
  High: "#B45309",
  "Very High": "#DC2626",
};

function cmMergedAnswers(assessment) {
  const md = assessment?.module_data || {};
  const m2 = md.module2 || {};
  // Step 3 answers now live under module3_groups[groupId]; fall back to the
  // legacy module3 namespace for pre-Warehouse-Group assessments.
  const m3legacy = md.module3 || {};
  const m3groups = md.module3_groups || {};
  const m3 = Object.keys(m3legacy).length ? m3legacy : m3groups;
  let landscape = {};
  if (m3[CM_LANDSCAPE_SITE] && typeof m3[CM_LANDSCAPE_SITE] === "object") {
    landscape = m3[CM_LANDSCAPE_SITE];
  } else {
    for (const v of Object.values(m3)) {
      if (v && typeof v === "object") landscape = { ...landscape, ...v };
    }
  }
  return { ...m2, ...landscape };
}

function cmNum(v) {
  const n = parseFloat(String(v ?? "").trim());
  return Number.isFinite(n) ? n : null;
}

function cmHighCustom(ans) {
  const keys = [
    "cm_dev_rf_customizations_count", "cm_dev_custom_reports_count",
    "cm_dev_custom_forms_count", "cm_dev_custom_interfaces_count",
    "cm_dev_enhancements_badis_count", "cm_dev_custom_fiori_apps_count",
  ];
  const total = keys.reduce((n, k) => n + (cmNum(ans[k]) || 0), 0);
  const bcrit = cmNum(ans["cm_dev_business_critical_objects_count"]) || 0;
  return total >= 50 || bcrit >= 10 || ans["custom_developments_count"] === "high";
}

function cmComplexitySignals(ans) {
  const iface = cmNum(ans["cm_int_interface_count"]);
  return {
    systems: (cmNum(ans["cm_productive_ewm_system_count"]) || 0) >= 2,
    warehouses: (cmNum(ans["cm_productive_warehouse_count"]) || 0) >= 4,
    countries: (cmNum(ans["cm_countries_supported"]) || 0) >= 2,
    database: (cmNum(ans["cm_data_database_size_gb"]) || 0) >= 1000,
    interfaces: (iface != null && iface >= 20) || ans["cm_int_business_critical_interfaces"] === "High",
    custom: cmHighCustom(ans),
    ops247: ans["cm_ops_24x7_operations"] === "Yes",
    downtime: ["Less than 1 Hour", "1-4 Hours"].includes(ans["cm_ops_downtime_tolerance"])
      || ans["acceptable_downtime"] === "low",
    parallel: ans["cm_ops_parallel_landscape_required"] === "Yes"
      || ans["cm_proj_active_implementation"] === "Yes"
      || ["Yes", "Partially"].includes(ans["cm_proj_shared_resources"]),
  };
}

function cmComplexityBand(ans) {
  const sig = cmComplexitySignals(ans);
  const count = Object.values(sig).filter(Boolean).length;
  let band = "Low";
  if (count >= 7) band = "Very High";
  else if (count >= 5) band = "High";
  else if (count >= 3) band = "Medium";
  return { band, count, signals: sig };
}

function cmCustomDevTotal(ans) {
  const keys = [
    "cm_dev_rf_customizations_count", "cm_dev_custom_reports_count",
    "cm_dev_custom_forms_count", "cm_dev_custom_interfaces_count",
    "cm_dev_enhancements_badis_count", "cm_dev_custom_fiori_apps_count",
  ];
  let has = false;
  let total = 0;
  for (const k of keys) {
    const v = cmNum(ans[k]);
    if (v != null) { has = true; total += v; }
  }
  return has ? total : null;
}

function cmRiskAreas(ans) {
  const sig = cmComplexitySignals(ans);
  const risks = [];
  // group: "technical" | "business"
  if (sig.interfaces) risks.push({ group: "technical", title: "Large interface landscape", detail: "External interfaces represent significant migration and cutover risk — validate early." });
  if (sig.custom) risks.push({ group: "technical", title: "High custom development footprint", detail: "Enhancements, custom objects and interfaces increase migration effort and testing." });
  if (sig.database) risks.push({ group: "technical", title: "Large production database", detail: "A large database requires careful migration sequencing and downtime planning." });
  const infraRisk = ["Not Available", "Planned"].includes(ans["cm_infra_dr_environment"])
    || ["Complex", "Highly Distributed"].includes(ans["cm_infra_network_complexity"]);
  if (infraRisk) risks.push({ group: "technical", title: "Infrastructure readiness", detail: "DR / network landscape needs validation before migration." });
  const dataRisk = sig.database
    || ans["cm_data_total_inventory_volume"] === "High"
    || ans["cm_data_total_handling_unit_volume"] === "High";
  if (dataRisk) risks.push({ group: "technical", title: "Data migration complexity", detail: "High data volumes require validated migration and reconciliation." });

  if (sig.downtime || sig.ops247) risks.push({ group: "business", title: "Low downtime tolerance", detail: "Continuous / time-sensitive operations demand a tightly controlled cutover." });
  if (sig.parallel) risks.push({ group: "business", title: "Parallel business projects", detail: "Active projects and shared resources increase coordination complexity." });
  if (sig.countries || sig.warehouses) risks.push({ group: "business", title: "Multi-country / multi-site rollout", detail: "Multiple warehouses / countries require phased deployment planning." });
  if (["High", "Critical"].includes(ans["cm_ops_business_continuity_sensitivity"])) {
    risks.push({ group: "business", title: "Business continuity sensitivity", detail: "High business-continuity requirements demand rigorous validation and fallback planning." });
  }
  return risks;
}

function cmComplexityExplanation(sig) {
  const phrases = [];
  if (sig.systems) phrases.push("multiple productive systems");
  if (sig.database) phrases.push("a large database volume");
  if (sig.custom) phrases.push("extensive custom developments");
  if (sig.interfaces) phrases.push("a large integration landscape");
  if (sig.ops247) phrases.push("24×7 warehouse operations");
  if (sig.downtime && !sig.ops247) phrases.push("limited downtime tolerance");
  if (sig.parallel) phrases.push("active parallel projects");
  if (sig.countries || sig.warehouses) phrases.push("a multi-site / global rollout");
  if (phrases.length === 0) {
    return "Suitable for a relatively straightforward technical migration.";
  }
  let list;
  if (phrases.length === 1) list = phrases[0];
  else list = phrases.slice(0, -1).join(", ") + " and " + phrases[phrases.length - 1];
  return `Driven by ${list}.`;
}

const CM_FOCUS_BY_KEY = {
  cm_interface_first: "Validate all external interfaces before production cutover",
  cm_custom_code_review: "Review and rationalize custom developments",
  cm_cutover_planning: "Prepare a detailed cutover strategy with rehearsals and rollback",
  cm_regression_testing: "Plan full end-to-end regression testing",
  cm_parallel_landscape: "Align the migration with active parallel projects",
  cm_pilot_first: "Prepare a pilot warehouse rollout to validate the approach",
  cm_phased_rollout: "Plan phased multi-warehouse deployment waves",
  cm_selective_modernization: "Scope selective modernization / optimization items",
  cm_lift_shift_validate: "Complete functional & integration validation before go-live",
  cm_lift_shift: "Execute a technical migration with minimal functional change",
};

function cmFocusAreas(recommendations) {
  const out = [];
  const seen = new Set();
  for (const r of recommendations || []) {
    const txt = CM_FOCUS_BY_KEY[r.recommendation_key];
    if (txt && !seen.has(txt)) { out.push(txt); seen.add(txt); }
  }
  const infra = "Validate target infrastructure, sizing and hosting model";
  if (!seen.has(infra)) out.push(infra);
  return out;
}

function CmSnapshotRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-b border-[#F3F4F6] last:border-0 text-[12px]">
      <span className="text-[#6B7280]">{label}</span>
      <span className="text-[#0F172A] font-semibold text-right">{value}</span>
    </div>
  );
}

// Shared Cloud Migration helpers — reused by the Executive Summary Report so
// complexity, risk, landscape and focus logic is never duplicated.
export {
  cmMergedAnswers,
  cmComplexityBand,
  cmComplexityExplanation,
  cmRiskAreas,
  cmCustomDevTotal,
  cmNum,
  cmFocusAreas,
  CmSnapshotRow,
  CM_COMPLEXITY_TONE,
  CM_LANDSCAPE_SITE,
};

function CloudMigrationInsights({ assessment, assessmentId, onRefresh }) {
  const [narrative, setNarrative] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!assessmentId) { setLoaded(true); return; }
    let cancelled = false;
    getAssessmentNarrative(assessmentId)
      .then((r) => { if (!cancelled) setNarrative((r?.narratives || [])[0] || null); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [assessmentId]);

  const ans = cmMergedAnswers(assessment);
  const { band, signals } = cmComplexityBand(ans);
  const complexityExplanation = cmComplexityExplanation(signals);
  const risks = cmRiskAreas(ans);
  const techRisks = risks.filter((r) => r.group === "technical");
  const bizRisks = risks.filter((r) => r.group === "business");
  const customDevTotal = cmCustomDevTotal(ans);
  const dbSizeVal = cmNum(ans.cm_data_database_size_gb);
  const ifaceVal = cmNum(ans.cm_int_interface_count);
  const recs = narrative?.recommendations || [];
  const primary = recs[0] || null;
  const intel = narrative?.cm_intelligence || null;
  const allReasons = [];
  const allDeploy = [];
  const seenR = new Set();
  const seenD = new Set();
  for (const r of recs) {
    for (const x of (r.reasons || [])) if (x.description && !seenR.has(x.description)) { allReasons.push(x); seenR.add(x.description); }
    for (const x of (r.deployment || [])) if (x.description && !seenD.has(x.description)) { allDeploy.push(x); seenD.add(x.description); }
  }
  const focus = cmFocusAreas(recs);
  const tone = CM_COMPLEXITY_TONE[band] || "#025979";

  // Two-level taxonomy (display-only) derived from the approved primary
  // migration approach. No recommendation logic is duplicated here.
  const { strategyKey, approach } = deriveCmStrategy(primary?.label);
  const isTechnical = strategyKey === "technical_migration";

  const snapshot = [
    ["Current ERP", ans.cm_erp_platform],
    ["ERP Version", ans.cm_erp_version],
    ["Current EWM", ans.cm_ewm_deployment],
    ["EWM Version", ans.cm_ewm_version],
    ["Productive Systems", ans.cm_productive_ewm_system_count],
    ["Warehouses", ans.cm_productive_warehouse_count],
    ["Countries", ans.cm_countries_supported],
    ["Users", ans.cm_ewm_user_count],
    ["Hosting Model", ans.cm_hosting_model],
    ["Database Platform", ans.cm_database_platform],
    ["Database Size", dbSizeVal != null ? `${dbSizeVal.toLocaleString()} GB` : undefined],
    ["Approx. Interfaces", ifaceVal != null ? ifaceVal.toLocaleString() : undefined],
    ["Custom Development Count", customDevTotal != null ? customDevTotal.toLocaleString() : undefined],
  ];

  return (
    <div data-testid="evaluation-insights-tab" className="max-w-[1100px] mx-auto space-y-6">
      <div className="flex items-center justify-end">
        <RerunScoringButton assessmentId={assessmentId} onRefresh={onRefresh} />
      </div>
      {/* 1. Migration Snapshot */}
      <section data-testid="cm-insights-snapshot" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 border-l-2 border-l-[#025979]">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
          Migration Snapshot
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0">
          {snapshot.map(([l, v]) => <CmSnapshotRow key={l} label={l} value={v} />)}
        </div>
      </section>

      {/* 2. Migration Complexity — prominent card with dynamic explanation */}
      <section data-testid="cm-insights-complexity" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 border-l-2" style={{ borderLeftColor: tone }}>
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
          Migration Complexity
        </h3>
        <div className="flex items-start gap-4 flex-wrap">
          <span
            data-testid="cm-insights-complexity-band"
            className="inline-flex items-center justify-center text-[15px] font-bold uppercase tracking-wider rounded-sm px-4 py-2 border"
            style={{ color: tone, borderColor: tone + "55", background: tone + "12" }}
          >
            {band}
          </span>
          <p data-testid="cm-complexity-explanation" className="flex-1 min-w-[240px] text-[13px] text-[#374151] leading-relaxed self-center">
            {complexityExplanation}
          </p>
        </div>
        <p className="mt-3 text-[11px] text-[#9CA3AF] italic leading-relaxed">
          Advisory indicator derived from the captured landscape, data volume, integration, custom development, and operations profile. This does not affect the recommendation logic.
        </p>
      </section>

      {/* 2. Recommended Transformation Strategy — layered on the approved approach */}
      <section data-testid="cm-insights-strategy" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 border-l-2 border-l-[#025979]">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
          Recommended Transformation Strategy
        </h3>
        {!loaded ? (
          <p className="text-[12px] text-[#9CA3AF]">Loading recommendation…</p>
        ) : !primary ? (
          <p className="text-[12px] text-[#9CA3AF] italic">No recommendation available yet.</p>
        ) : (
          <div data-testid="cm-strategy-primary" className="rounded-sm border border-[#025979]/30 bg-gradient-to-br from-[#E0F2FE] to-[#F0F9FF] px-5 py-4">
            <p className="text-[9.5px] uppercase tracking-[0.12em] text-[#025979] font-bold mb-1.5">Recommended Strategy</p>
            <p data-testid="cm-strategy-name" className="text-[19px] font-bold text-[#0F172A] leading-snug" style={{ fontFamily: "Chivo, sans-serif" }}>
              {CM_STRATEGY_LABELS[strategyKey]}
            </p>
            <p className="mt-1.5 text-[12.5px] text-[#334155] leading-relaxed">{CM_STRATEGY_DESCRIPTIONS[strategyKey]}</p>
          </div>
        )}
      </section>

      {/* 2b. Recommendation Intelligence — evidence-driven (Phase 2D) */}
      {loaded && intel && (
        <section data-testid="cm-insights-intelligence" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 border-l-2 border-l-[#025979]">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
              Recommendation Intelligence
            </h3>
            <span data-testid="cm-intel-confidence"
              className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm px-3 py-1 border ${
                intel.confidence === "High" ? "text-[#065F46] border-[#065F46]/40 bg-[#065F46]/10"
                : intel.confidence === "Medium" ? "text-[#B45309] border-[#B45309]/40 bg-[#B45309]/10"
                : "text-[#B91C1C] border-[#B91C1C]/40 bg-[#B91C1C]/10"}`}>
              Confidence: {intel.confidence}
            </span>
          </div>

          {/* Dimension scores */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="cm-intel-dimensions">
            {(intel.dimensions || []).map((d) => (
              <div key={d.key} data-testid={`cm-intel-dim-${d.key}`} className="border border-[#E5E7EB] rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] leading-tight">{d.label}</p>
                <p className="text-[15px] font-bold text-[#0F172A] mt-1">{d.level}</p>
                <div className="mt-1.5 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                  <div className="h-full bg-[#025979]" style={{ width: `${d.score_pct}%` }} />
                </div>
              </div>
            ))}
          </div>

          {/* Confidence factors */}
          {(intel.confidence_factors || []).length > 0 && (
            <div className="mb-5" data-testid="cm-intel-confidence-factors">
              <p className="text-[9.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Confidence Basis</p>
              <ul className="space-y-1 text-[12px] text-[#374151] leading-relaxed">
                {intel.confidence_factors.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="font-semibold text-[#0F172A] min-w-[170px]">{f.label} · {f.value_pct}%</span>
                    <span className="text-[#6B7280]">{f.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {(intel.reasons || []).length > 0 && (
              <div data-testid="cm-intel-reasons">
                <p className="text-[9.5px] uppercase tracking-wider text-[#065F46] font-bold mb-2">Recommended Because</p>
                <ul className="space-y-1.5 text-[12.5px] text-[#111827] leading-relaxed">
                  {intel.reasons.map((r, i) => (
                    <li key={i} className="flex items-start gap-2"><span className="text-[#16A34A] mt-0.5">✓</span><span>{r}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {(intel.opportunities || []).length > 0 && (
              <div data-testid="cm-intel-opportunities">
                <p className="text-[9.5px] uppercase tracking-wider text-[#025979] font-bold mb-2">Key Opportunities</p>
                <ul className="space-y-1.5 text-[12.5px] text-[#111827] leading-relaxed">
                  {intel.opportunities.map((o, i) => (
                    <li key={i} className="flex items-start gap-2"><span className="text-[#025979] mt-0.5">→</span><span>{o}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {(intel.risks || []).length > 0 && (
            <div className="mt-5" data-testid="cm-intel-risks">
              <p className="text-[9.5px] uppercase tracking-wider text-[#B45309] font-bold mb-2">Key Risks of This Strategy</p>
              <ul className="space-y-1.5 text-[12.5px] text-[#111827] leading-relaxed">
                {intel.risks.map((r, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-[#B45309] mt-0.5">▲</span><span>{r}</span></li>
                ))}
              </ul>
            </div>
          )}

          {/* Consultant-only — NOT shown in client-facing report */}
          {(intel.why_not || []).length > 0 && (
            <div className="mt-5 rounded-sm border border-dashed border-[#94A3B8]/60 bg-[#F8FAFC] px-4 py-3" data-testid="cm-intel-why-not">
              <p className="text-[9.5px] uppercase tracking-wider text-[#475569] font-bold mb-2">
                Consultant Guidance · Why Not the Other Strategies?
                <span className="ml-2 font-medium text-[#94A3B8] normal-case tracking-normal">(internal — excluded from client report)</span>
              </p>
              <ul className="space-y-1.5 text-[12px] text-[#374151] leading-relaxed">
                {intel.why_not.map((w, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`cm-why-not-${i}`}>
                    <span className="text-[#64748B] mt-0.5">•</span>
                    <span><span className="font-semibold text-[#0F172A]">Why not {w.strategy}?</span> {w.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* 3. Recommended Migration Approach — only for the Technical Migration strategy */}
      {loaded && primary && isTechnical && (
        <section data-testid="cm-insights-approach" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
          <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
            Recommended Migration Approach
          </h3>
          <p data-testid="cm-approach-name" className="text-[16px] font-bold text-[#0F172A] leading-snug" style={{ fontFamily: "Chivo, sans-serif" }}>
            {approach}
          </p>
          <p className="mt-1.5 text-[12.5px] text-[#334155] leading-relaxed">
            {primary.description || CM_APPROACH_DESCRIPTIONS[approach]}
          </p>
        </section>
      )}

      {/* Recommendation detail — reasons + deployment guidance (reuses narrative) */}
      {loaded && primary && (allReasons.length > 0 || allDeploy.length > 0) && (
        <section data-testid="cm-insights-direction" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
          <div className="space-y-5">
            {allReasons.length > 0 && (
              <div data-testid="cm-direction-reasons">
                <p className="text-[9.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Key Reasons</p>
                <ul className="space-y-1 text-[12px] text-[#374151] leading-relaxed">
                  {allReasons.map((x, i) => (
                    <li key={i} className="flex items-start gap-2"><span className="text-[#025979] mt-0.5">•</span><span>{x.description}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {allDeploy.length > 0 && (
              <div data-testid="cm-direction-deployment">
                <p className="text-[9.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Deployment Guidance</p>
                <ul className="space-y-1 text-[12px] text-[#374151] leading-relaxed">
                  {allDeploy.map((x, i) => (
                    <li key={i} className="flex items-start gap-2"><span className="text-[#025979] mt-0.5">•</span><span>{x.description}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 4. Key Risk Areas — grouped into Technical & Business */}
      {risks.length > 0 && (
        <section data-testid="cm-insights-risks" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
          <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
            Key Risk Areas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {techRisks.length > 0 && (
              <div data-testid="cm-risks-technical">
                <p className="text-[9.5px] uppercase tracking-wider text-[#B45309] font-bold mb-2.5">Technical Risks</p>
                <ul className="space-y-2.5 text-[12.5px] text-[#111827] leading-relaxed">
                  {techRisks.map((r, i) => (
                    <li key={i} className="flex items-start gap-2" data-testid={`cm-risk-technical-${i}`}>
                      <span className="text-[#B45309] mt-0.5">▲</span>
                      <span><span className="font-semibold text-[#0F172A]">{r.title}</span> — {r.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {bizRisks.length > 0 && (
              <div data-testid="cm-risks-business">
                <p className="text-[9.5px] uppercase tracking-wider text-[#025979] font-bold mb-2.5">Business Risks</p>
                <ul className="space-y-2.5 text-[12.5px] text-[#111827] leading-relaxed">
                  {bizRisks.map((r, i) => (
                    <li key={i} className="flex items-start gap-2" data-testid={`cm-risk-business-${i}`}>
                      <span className="text-[#025979] mt-0.5">▲</span>
                      <span><span className="font-semibold text-[#0F172A]">{r.title}</span> — {r.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 5. Consultant Focus Areas */}
      {focus.length > 0 && (
        <section data-testid="cm-insights-focus" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
          <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
            Consultant Focus Areas
          </h3>
          <ul className="space-y-2.5 text-[12.5px] text-[#111827] leading-relaxed">
            {focus.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5" data-testid={`cm-focus-${i}`}>
                <span className="text-[#16A34A] mt-0.5">✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
